import { Box } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

const rings = [
  { size: 120 },
  { size: 210 },
  { size: 300 },
  { size: 390, hideBelow: "tablet" },
  { size: 480, hideBelow: "tablet" },
  { size: 570, hideBelow: "desktop" },
  { size: 660, hideBelow: "desktop" },
  { size: 750, hideBelow: "desktop" },
  { size: 840, hideBelow: "desktop" },
] as const;

interface ConcentricCirclesProps {
  corner: "top-left" | "top-right";
}

export function ConcentricCircles({ corner }: ConcentricCirclesProps) {
  const isLeft = corner === "top-left";
  const anchor = isLeft ? { left: "0" } : { right: "0" };
  const ringTransform = isLeft ? "translate(-50%, -50%)" : "translate(50%, -50%)";
  const glowTransform = isLeft ? "translate(-40%, -40%)" : "translate(40%, -40%)";

  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @keyframes ccFadeIn {
          from { opacity: 0; transform: scale(0.88); }
          to   { opacity: 1; transform: scale(1); }
        }
        .concentric-rings-visible {
          animation: ccFadeIn 3.6s cubic-bezier(0.22, 1, 0.36, 1) 1.3s both;
        }
        .concentric-rings-hidden {
          opacity: 0;
        }
      `}</style>
      <Box
        ref={ref}
        className={visible ? "concentric-rings-visible" : "concentric-rings-hidden"}
        position="absolute"
        top="0"
        {...anchor}
        pointerEvents="none"
        w="840px"
        h="840px"
        style={{ transformOrigin: corner === "top-left" ? "top left" : "top right" }}
      >
        {rings.map(({ size, ...rest }) => {
          const hideBelow = "hideBelow" in rest ? rest.hideBelow : undefined;
          return (
            <Box
              key={size}
              position="absolute"
              top="0"
              {...anchor}
              pointerEvents="none"
              display={
                hideBelow === "tablet"
                  ? { base: "none", tablet: "block" }
                  : hideBelow === "desktop"
                    ? { base: "none", desktop: "block" }
                    : "block"
              }
              style={{
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: "50%",
                border: "1.5px solid rgba(34,197,94,0.13)",
                transform: ringTransform,
              }}
            />
          );
        })}
        <Box
          position="absolute"
          top="0"
          {...anchor}
          pointerEvents="none"
          display={{ base: "none", tablet: "block" }}
          style={{
            width: "480px",
            height: "480px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(34,197,94,0.07) 0%, transparent 70%)",
            transform: glowTransform,
          }}
        />
      </Box>
    </>
  );
}
